import type { PostgrestError } from '@supabase/supabase-js'

/**
 * Traducción de errores de la base a frases que un operario entienda.
 *
 * Las reglas del negocio viven en la base —transiciones, compuertas,
 * inventario— así que cuando algo se impide, el error llega desde allá. Sin
 * esta capa, el usuario vería "new row violates check constraint
 * ck_ot_mas_tiempo_justificado", que no le dice qué hacer.
 *
 * Los mensajes explican QUÉ falta, no qué falló.
 */

/** Restricciones de la base, con su explicación para el usuario. */
const MENSAJES_POR_RESTRICCION: Record<string, string> = {
  // ── Maestros ────────────────────────────────────────────────────────────
  uq_clientes_documento: 'Ya existe un cliente con ese documento.',
  uq_servicios_codigo: 'Ya existe un servicio con ese código.',
  uq_colaboradores_documento: 'Ya existe un colaborador con ese documento.',
  uq_colaboradores_perfil: 'Ese usuario ya está enlazado a otro colaborador.',
  uq_herramientas_codigo: 'Ya existe una herramienta con ese código.',
  uq_zonas_nombre: 'Ya existe una zona con ese nombre.',
  ck_herramientas_disponible_valido:
    'El inventario no cuadra: no puede quedar en negativo ni recibir más unidades de las que salieron.',

  // ── Cadena de documentos ────────────────────────────────────────────────
  uq_ordenes_servicio_numero: 'Ese número de orden de servicio ya está usado.',
  uq_cotizaciones_numero: 'Ese número de cotización ya está registrado en otra orden.',
  uq_cotizacion_vigente_por_orden:
    'Esa orden de servicio ya tiene una cotización vigente. Recháce la anterior antes de crear otra.',
  ck_cotizaciones_completa_al_cotizar:
    'Para pasar la cotización de pendiente hace falta el número de World Office y el valor.',
  ck_cotizaciones_decision_trazada:
    'Un rechazo necesita quién lo decide y por qué; una aprobación, solo quién.',
  ck_cotizaciones_mano_obra_cabe:
    'El valor de mano de obra no puede ser mayor que el valor total.',

  // ── Orden de trabajo ────────────────────────────────────────────────────
  uq_ordenes_trabajo_cotizacion: 'Esa cotización ya generó su orden de trabajo.',
  ck_ot_programacion_completa:
    'Para programar hacen falta las tres cosas: colaborador, horas asignadas y fecha de inicio.',
  ck_ot_mas_tiempo_justificado:
    'Pedir más tiempo exige indicar cuántas horas adicionales y por qué.',
  ck_ot_fechas_coherentes: 'La fecha de fin no puede ser anterior a la de inicio.',
  ck_ot_horas_no_negativas: 'Las horas no pueden ser negativas.',
}

/** Códigos de PostgreSQL que llegan sin nombre de restricción. */
const MENSAJES_POR_CODIGO: Record<string, string> = {
  '23505': 'Ya existe un registro con esos datos.',
  '23503': 'No se puede guardar: hace referencia a un registro que no existe.',
  '23502': 'Falta un dato obligatorio.',
  '23514': 'Los datos no cumplen una regla del sistema.',
  '42501': 'Su rol no tiene permiso para hacer esto.',
  '42P01': 'La tabla consultada no existe. Puede faltar correr las migraciones.',
  PGRST301: 'Su sesión expiró. Vuelva a ingresar.',
}

/**
 * Cómo se reconoce un error CRUDO de PostgreSQL.
 *
 * Los suyos vienen en inglés y hablan de tablas y restricciones: «new row for
 * relation "x" violates check constraint "y"». Los nuestros los escribimos a
 * mano, en español y en términos del negocio.
 *
 * Se distingue por aquí y NO por cómo empieza la frase, que era como estaba
 * antes: había una lista de seis comienzos permitidos —«Solo », «Su rol »,
 * «La orden»…— y cualquier mensaje que no empezara exactamente así se tiraba
 * y se sustituía por una frase genérica.
 *
 * Lo que eso provocaba: la base decía «Estás a 400 m de la bodega. La
 * herramienta se recibe allá» y al técnico le aparecía «Los datos no cumplen
 * una regla del sistema». En un proyecto donde las reglas de negocio viven en
 * la base A PROPÓSITO, tirar los mensajes que esas reglas escriben es tirar
 * la mitad del trabajo.
 */
const PARECE_DE_POSTGRES =
  /violates|constraint|duplicate key|null value in column|invalid input|out of range|does not exist|permission denied for|invalid text representation/i

/**
 * Convierte cualquier error en una frase mostrable.
 *
 * El orden es el que importa:
 *
 *   1. Si se reconoce la restricción por su nombre, gana la explicación
 *      escrita para ella: el texto crudo de una restricción es ilegible.
 *   2. Si el mensaje NO parece de PostgreSQL, es nuestro y se muestra tal
 *      cual. Un mensaje escrito para una persona siempre es mejor que una
 *      traducción genérica por código.
 *   3. Y solo si además parece crudo se cae a la frase por código.
 */
export function mensajeDeError(error: unknown): string {
  if (!error) {
    return 'Ocurrió un error inesperado.'
  }

  const posible = error as Partial<PostgrestError> & { message?: string }
  const texto = posible.message ?? ''

  // 1 · Por nombre de restricción, en el mensaje o en el detalle.
  for (const [restriccion, mensaje] of Object.entries(MENSAJES_POR_RESTRICCION)) {
    if (posible.details?.includes(restriccion) || texto.includes(restriccion)) return mensaje
  }

  // 2 · Escrito por nosotros: se muestra entero.
  if (texto && !PARECE_DE_POSTGRES.test(texto)) {
    return texto
  }

  // 3 · Crudo y sin explicación propia: lo genérico es mejor que el original.
  if (posible.code && MENSAJES_POR_CODIGO[posible.code]) {
    return MENSAJES_POR_CODIGO[posible.code]
  }

  return texto || 'Ocurrió un error inesperado.'
}
